import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtUSD, MOCK_TEAMS, type LeagueBonus, type MockTeam } from './types';

export function AwardDialog({
  open,
  onOpenChange,
  bonus,
  onAward,
  teams = MOCK_TEAMS,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bonus: LeagueBonus | null;
  onAward: (teamId: string) => void;
  teams?: MockTeam[];
}) {
  const [teamId, setTeamId] = useState('');

  useEffect(() => {
    if (open) setTeamId('');
  }, [open]);

  if (!bonus) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Award: {bonus.bonus_name}</DialogTitle>
        </DialogHeader>

        <div className="px-3 py-2.5 bg-muted rounded-lg text-[13px] text-muted-foreground flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" />
          <div>
            Awarding{' '}
            <strong className="text-orange-500">{fmtUSD(bonus.amount)}</strong>{' '}
            from the prize pool. Recipient gets push + email notification.
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
            Recipient team
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
            {teams.map((t) => {
              const selected = teamId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTeamId(t.id)}
                  // focus-visible (keyboard only) for the focus ring; suppress
                  // the lingering mouse-click focus ring that previously made
                  // a non-selected card look selected after a click elsewhere.
                  className={cn(
                    'text-left p-2.5 rounded-lg border flex items-center gap-2.5 transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    selected
                      ? 'border-orange-500 bg-orange-500/15 ring-1 ring-orange-500'
                      : 'border-border bg-card hover:border-orange-500/40',
                  )}
                  aria-pressed={selected}
                >
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[11px] shrink-0"
                    style={{ background: t.color, color: '#0B1322' }}
                  >
                    {t.avatar}
                  </span>
                  <span className="min-w-0 flex flex-col leading-tight">
                    <span className="font-bold text-xs truncate">{t.name}</span>
                    <span className="text-[10px] text-muted-foreground">
                      @{t.owner}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!teamId}
            onClick={() => onAward(teamId)}
            className="gap-1"
          >
            <Send className="w-3.5 h-3.5" /> Award {fmtUSD(bonus.amount)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
